import React, { useState } from 'react';
import Card from '../../common/Card/Card';
import Pagination from '../../common/Pagination/Pagination';
import { Place, PlaceDetail, Person, EmailData } from '../../../types/results.types';
import { getPlaceDetail } from '../../../api/results';
import { BalanceResponse } from '../../../types/balance.types';
import CompactResultRow from '../CompactResultRow/CompactResultRow';
import ResultsDrawer from '../ResultsDrawer/ResultsDrawer';
import InlineVerifyButton, { VerifyModalPayload } from '../InlineVerifyButton/InlineVerifyButton';
import EmailVerificationModal from '../EmailVerificationModal/EmailVerificationModal';
import DecisionMakerBadge from '../DecisionMakerBadge/DecisionMakerBadge';
import FloatingSelectionBar from '../FloatingSelectionBar/FloatingSelectionBar';
import ContactCell from '../ContactCell/ContactCell';
import EmailStatusCell from '../EmailStatusCell/EmailStatusCell';
import { toTitleCase } from '../../../utils/formatting';
import { Repeat2, Download, Mail, Megaphone } from 'lucide-react';
import MatchStrengthCell from '../MatchStrengthCell/MatchStrengthCell';
import PhoneContactCell from '../PhoneContactCell/PhoneContactCell';
import GoogleAdsSignalBadge from '../GoogleAdsSignalBadge/GoogleAdsSignalBadge';

/**
 * Merges the (trimmed) list row with the lazily-fetched drawer detail into the
 * full legacy Place shape the drawer's tabs expect, so OverviewTab/
 * MatchEvidenceTab/ReviewsTab need no changes for the row/detail split. Before
 * `detail` resolves, fills in safe empty defaults for the fields the row omits
 * (industries, website_scraped_data, website_fit.evidence/matched_customers/
 * pages_analyzed) rather than leaving them `undefined`, since some of those are
 * read without optional chaining.
 */
function mergeDrawerPlace(place: Place | null, detail: PlaceDetail | null): Place | null {
  if (!place) return null;
  if (!detail) {
    return {
      ...place,
      industries: place.industries ?? [],
      website_scraped_data: place.website_scraped_data ?? [],
      website_fit: place.website_fit
        ? { ...place.website_fit, evidence: [], matched_customers: [], pages_analyzed: 0 }
        : place.website_fit,
    };
  }
  return {
    ...place,
    ...detail,
    lead_signals: { ...place.lead_signals, ...detail.lead_signals },
    website_fit: place.website_fit
      ? { ...place.website_fit, ...detail.website_fit }
      : detail.website_fit,
  };
}

interface ResultsTableProps {
  places: Place[];
  pagination: {
    page: number;
    totalPages: number;
    itemsPerPage: number;
    totalItems: number;
    has_locked_results?: boolean;
    places_locked?: number;
  };
  onPageChange: (page: number) => void;
  loading: boolean;
  onExport: (format: 'csv' | 'excel') => void;
  onVerifyEmails?: () => void;
  verifiableCount?: number;
  websiteConfirmedCount?: number;
  googleAdsSignal?: 'detected_recently' | 'no_ad_tracking' | null;
  googleAdsDetectedCount?: number | null;
  onCompanyDrawerStateChange?: (isOpen: boolean) => void;
  closeDrawerSignal?: number;
  searchId?: number;
  showDrawerCoachmark?: boolean;
  onDismissDrawerCoachmarkGotIt?: () => void;
  balance?: BalanceResponse | null;
  onUpgradeClick?: () => void;
  currentPlan?: string | null;
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
  shortLabels?: Record<string, string>;
  showWebsiteMatch?: boolean;
  isNoWebsiteSearch?: boolean;
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  places,
  pagination,
  onPageChange,
  loading,
  onExport,
  onVerifyEmails,
  verifiableCount = 0,
  websiteConfirmedCount,
  googleAdsSignal,
  googleAdsDetectedCount,
  onCompanyDrawerStateChange,
  closeDrawerSignal,
  searchId,
  showDrawerCoachmark,
  onDismissDrawerCoachmarkGotIt,
  balance,
  onUpgradeClick,
  currentPlan,
  autoFindMode,
  autoFindSuccessLimit,
  autoFindSuccessCount,
  autoFindAttemptCount,
  autoFindNoEmailCount,
  autoFindCatchAllCount,
  autoFindGuardrailReason,
  shortLabels = {},
  showWebsiteMatch = false,
  isNoWebsiteSearch = false,
}) => {
  const [drawerPlace, setDrawerPlace] = useState<Place | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerPlaceDetail, setDrawerPlaceDetail] = useState<PlaceDetail | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Stable owner for the extra mobile quick-verify button rendered per card.
  // Snapshots are captured at open time so results refreshes can't mutate the payload.
  const [mobileVerifyOpen, setMobileVerifyOpen] = useState(false);
  const [mobileVerifyPersonEmails, setMobileVerifyPersonEmails] = useState<EmailData[]>([]);
  const [mobileVerifyPlaceEmails, setMobileVerifyPlaceEmails] = useState<EmailData[]>([]);
  const [mobileVerifyBusinessName, setMobileVerifyBusinessName] = useState<string | undefined>();
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
  const exportMenuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportMenuOpen]);

  const openMobileVerify = ({ personEmails, placeEmails, businessName }: VerifyModalPayload) => {
    setMobileVerifyPersonEmails(personEmails);
    setMobileVerifyPlaceEmails(placeEmails);
    setMobileVerifyBusinessName(businessName);
    setMobileVerifyOpen(true);
  };

  const closeMobileVerify = () => {
    setMobileVerifyOpen(false);
    setMobileVerifyPersonEmails([]);
    setMobileVerifyPlaceEmails([]);
    setMobileVerifyBusinessName(undefined);
  };

  React.useEffect(() => {
    if (typeof closeDrawerSignal !== 'undefined') {
      setIsDrawerOpen(false);
      onCompanyDrawerStateChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDrawerSignal]);

  const drawerDetailRequestRef = React.useRef(0);

  const handleViewDetails = (place: Place) => {
    setDrawerPlace(place);
    setDrawerPlaceDetail(null);
    setIsDrawerOpen(true);
    onCompanyDrawerStateChange?.(true);
    if (searchId) {
      const requestId = ++drawerDetailRequestRef.current;
      getPlaceDetail(searchId, place.id)
        .then(detail => {
          if (drawerDetailRequestRef.current === requestId) setDrawerPlaceDetail(detail);
        })
        .catch(() => {
          if (drawerDetailRequestRef.current === requestId) setDrawerPlaceDetail(null);
        });
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    onCompanyDrawerStateChange?.(false);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev =>
      prev.size === places.length ? new Set() : new Set(places.map(p => p.id)),
    );
  };

  const allChecked = places.length > 0 && selectedIds.size === places.length;

  // ── Helpers used by the mobile card list ─────────────────────────────────
  const getFilteredPersons = (people: Person[] | undefined): Person[] => {
    if (!people) return [];
    return people.filter(
      person => person.ai_score !== null && person.ai_score !== undefined && person.ai_score <= 3,
    );
  };

  const getDecisionMakerInfo = (people: Person[] | undefined) => {
    const validPeople =
      people?.filter(
        p => p.ai_score !== null && p.ai_score !== undefined && p.ai_score <= 3 && p.score >= 30,
      ) || [];
    const decisionMakers = validPeople.filter(p => p.score >= 60);
    const cSuite = decisionMakers.filter(p => p.score >= 85);
    const executives = decisionMakers.filter(p => p.score >= 70 && p.score < 85);
    const highestLevel =
      cSuite.length > 0 ? 'c-suite' : executives.length > 0 ? 'executive' : decisionMakers.length > 0 ? 'manager' : null;
    return { total: validPeople.length, decisionMakers: decisionMakers.length, highestLevel };
  };

  const collectEmails = (people: Person[] | undefined, companyEmails?: EmailData[]): EmailData[] => {
    const emailMap = new Map<string, EmailData>();
    getFilteredPersons(people).forEach(person => {
      (person.emails || []).forEach(email => {
        if (!emailMap.has(email.email)) emailMap.set(email.email, email);
      });
    });
    (companyEmails || []).forEach(email => {
      if (!emailMap.has(email.email)) emailMap.set(email.email, email);
    });
    return Array.from(emailMap.values());
  };

  const getAllPhoneNumbers = (place: Place): string[] => {
    if (place.phone_numbers?.length) return place.phone_numbers;
    if (place.phone_number) return [place.phone_number];
    return [];
  };

  const renderRatingStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    const starPath =
      'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.173 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';
    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <svg key={`f${i}`} className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
        ))}
        {hasHalfStar && (
          <div className="relative inline-block w-3.5 h-3.5">
            <svg className="w-3.5 h-3.5 text-gray-300 absolute" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
            <div className="absolute top-0 left-0 w-1/2 h-full overflow-hidden">
              <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <svg key={`e${i}`} className="w-3.5 h-3.5 text-gray-300" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
        ))}
      </div>
    );
  };

  if (!loading && places.length === 0) {
    return (
      <Card>
        <div className="text-center py-8 sm:py-12">
          <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900">No results found</h3>
          <p className="mt-1 text-gray-500">Try adjusting your search or filters.</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      {/* ── "Your leads" table header ──────────────────────────────────── */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-bold text-[#2D3142]">Your leads</h2>
          <p className="text-[11px] text-[#9CA3AF]">
            {pagination.totalItems.toLocaleString()} saved
            {websiteConfirmedCount != null && websiteConfirmedCount > 0 && (
              <> · ranked by website match</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isNoWebsiteSearch && verifiableCount > 0 && onVerifyEmails && (
            <button
              onClick={onVerifyEmails}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#D1D5DB] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#374151] hover:bg-gray-50 transition-colors"
            >
              <Mail className="h-3.5 w-3.5" />
              Verify {verifiableCount.toLocaleString()} emails
            </button>
          )}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setExportMenuOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#D1D5DB] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#374151] hover:bg-gray-50 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Export
              <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-20 w-28 rounded-lg border border-[#E5E7EB] bg-white shadow-lg py-1">
                <button
                  onClick={() => { setExportMenuOpen(false); onExport('csv'); }}
                  className="w-full px-3 py-2 text-left text-[12px] font-medium text-[#374151] hover:bg-gray-50"
                >
                  CSV
                </button>
                <button
                  onClick={() => { setExportMenuOpen(false); onExport('excel'); }}
                  className="w-full px-3 py-2 text-left text-[12px] font-medium text-[#374151] hover:bg-gray-50"
                >
                  XLSX
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {websiteConfirmedCount != null && websiteConfirmedCount > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
          <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <p className="text-[11px] text-emerald-800">
            <span className="font-semibold">Ranked by website match.</span>{' '}
            We opened each company's site to confirm they offer what you target —{' '}
            <span className="font-semibold">{websiteConfirmedCount.toLocaleString()} strong</span> website matches first.
          </p>
        </div>
      )}

      {googleAdsSignal === 'detected_recently' && googleAdsDetectedCount != null && googleAdsDetectedCount > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2">
          <Megaphone className="h-4 w-4 text-indigo-600 flex-shrink-0" />
          <p className="text-[11px] text-indigo-800">
            <span className="font-semibold">At least {googleAdsDetectedCount.toLocaleString()} businesses</span>{' '}
            confirmed having run Google Ads.
          </p>
        </div>
      )}

      {googleAdsSignal === 'detected_recently' && googleAdsDetectedCount === 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <Megaphone className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <p className="text-[11px] text-gray-600">No confirmed advertisers in this area yet.</p>
        </div>
      )}

      <Card>
        <div className="flex flex-col">
          {/* ── Mobile card list ──────────────────────────────────────────────
              Inlined (not a local function component) so React reconciles rows
              by key={place.id} and EmailStatusCell state survives re-renders. */}
          <div className="md:hidden space-y-3">
            {loading ? (
              Array.from({ length: pagination.itemsPerPage }).map((_, i) => (
                <div key={`skeleton-mobile-${i}`} className="rounded-xl border border-gray-200 bg-white p-3" aria-busy="true">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <div className="mt-0.5 w-[17px] h-[17px] shrink-0 animate-pulse rounded bg-[#F0EDE8]" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3.5 w-40 animate-pulse rounded bg-[#F0EDE8]" />
                        <div className="h-2.5 w-28 animate-pulse rounded bg-[#F0EDE8]" />
                      </div>
                    </div>
                    <div className="h-3 w-16 shrink-0 animate-pulse rounded bg-[#F0EDE8]" />
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    <div className="h-5 w-20 animate-pulse rounded-md bg-[#F0EDE8]" />
                  </div>
                  <div className="mt-2.5 flex items-center gap-2">
                    <div className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-[#F0EDE8]" />
                    <div className="space-y-1.5 flex-1">
                      <div className="h-3 w-24 animate-pulse rounded bg-[#F0EDE8]" />
                      <div className="h-2.5 w-16 animate-pulse rounded bg-[#F0EDE8]" />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-6 w-16 animate-pulse rounded-full bg-[#F0EDE8]" />
                    <div className="h-6 w-12 animate-pulse rounded-full bg-[#F0EDE8]" />
                    <div className="h-6 w-12 animate-pulse rounded-full bg-[#F0EDE8]" />
                  </div>
                </div>
              ))
            ) : places.map(place => {
              const emailCount = collectEmails(place.people, place.company_emails).length;
              const phoneCount = getAllPhoneNumbers(place).length;
              const mainIndustry =
                place.main_industry || ((place.industries?.length ?? 0) > 0 ? place.industries![0].name : null);
              const peopleInfo = getDecisionMakerInfo(place.people);
              const isSelected = selectedIds.has(place.id);

              return (
                <div
                  key={place.id}
                  className={`rounded-xl border shadow-sm overflow-hidden transition-all ${
                    isSelected ? 'border-[#8A3033] bg-[#FEF4F4]' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="p-3 cursor-pointer" onClick={() => handleViewDetails(place)}>
                    {/* Top row: checkbox + name + rating */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        {!isNoWebsiteSearch && (
                          <div
                            className={`mt-0.5 w-[17px] h-[17px] rounded flex-shrink-0 flex items-center justify-center border cursor-pointer transition-all ${
                              isSelected ? 'bg-[#8A3033] border-[#8A3033]' : 'bg-white border-[#D1D5DB]'
                            }`}
                            onClick={e => { e.stopPropagation(); toggleSelect(place.id); }}
                          >
                            {isSelected && (
                              <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l2.5 2.5L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <button
                            onClick={e => { e.stopPropagation(); handleViewDetails(place); }}
                            className="text-sm font-semibold text-gray-900 hover:text-primary text-left truncate block w-full"
                          >
                            {toTitleCase(place.name)}
                          </button>
                          {place.address && place.address !== 'None' && (
                            <p className="truncate text-xs text-gray-500">{toTitleCase(place.address)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0">
                        {renderRatingStars(place.review_rating)}
                      </div>
                    </div>

                    {/* Tags row */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {mainIndustry && (
                        <span className="inline-flex items-center bg-[#8A3033] text-white text-[10px] uppercase tracking-wider font-bold rounded-md px-2.5 py-1 whitespace-nowrap">
                          {mainIndustry}
                        </span>
                      )}
                      {place.is_previously_seen && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-status-success text-white">
                          <Repeat2 className="w-3 h-3" />
                          <span className="text-[10px]">FREE</span>
                        </span>
                      )}
                      <GoogleAdsSignalBadge signal={place.lead_signals?.google_ads_signal} />
                    </div>

                    {isNoWebsiteSearch && (
                      <div className="mt-2.5 rounded-lg border border-[#EEE9E3] bg-[#FAF8F5] px-2.5 py-2">
                        <PhoneContactCell place={place} compact />
                      </div>
                    )}

                    {/* ContactCell (hints off on mobile for brevity) */}
                    {(!isNoWebsiteSearch || (place.people?.length ?? 0) > 0) && (
                      <div className="mt-2.5">
                        <ContactCell place={place} hints={false} publicRecordsOnly={isNoWebsiteSearch} />
                      </div>
                    )}

                    {/* EmailStatusCell one-liner — owns find/verify modal state per row */}
                    {!isNoWebsiteSearch && <div className="mt-2">
                      <EmailStatusCell
                        place={place}
                        balance={balance ?? null}
                        searchId={searchId}
                        currentPlan={currentPlan}
                        autoFindMode={autoFindMode}
                        autoFindSuccessLimit={autoFindSuccessLimit}
                        autoFindSuccessCount={autoFindSuccessCount}
                        autoFindAttemptCount={autoFindAttemptCount}
                        autoFindNoEmailCount={autoFindNoEmailCount}
                        autoFindCatchAllCount={autoFindCatchAllCount}
                        autoFindGuardrailReason={autoFindGuardrailReason}
                        onViewDetails={() => handleViewDetails(place)}
                        onUpgradeClick={onUpgradeClick}
                      />
                    </div>}

                    {/* Quick stats row */}
                    <div className="mt-2 flex items-center flex-wrap gap-2">
                      {!isNoWebsiteSearch && <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        emailCount > 0 ? 'bg-status-success/10 text-status-success border-status-success/20' : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        📧 <span className="font-semibold">{emailCount}</span>
                      </span>}
                      {peopleInfo.total > 0 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-50 text-gray-700 border-gray-200">
                          👤 <span className="font-semibold">{peopleInfo.decisionMakers > 0 ? `${peopleInfo.decisionMakers} DM` : peopleInfo.total}</span>
                        </span>
                      )}
                      {!isNoWebsiteSearch && <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        phoneCount > 0 ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        📞 <span className="font-semibold">{phoneCount}</span>
                      </span>}
                      <span className="text-xs text-gray-500 ml-auto">{place.review_count} reviews</span>
                    </div>

                    {peopleInfo.highestLevel && (
                      <div className="mt-2 flex items-center gap-2">
                        <DecisionMakerBadge level={peopleInfo.highestLevel as 'c-suite' | 'executive' | 'manager'} compact />
                      </div>
                    )}
                    {place.website_fit && (
                      <div className="mt-2">
                        <MatchStrengthCell fit={place.website_fit} compact shortLabels={shortLabels} />
                      </div>
                    )}

                    {/* Quick verify button — bubbles up to ResultsTable's stable modal owner */}
                    {!isNoWebsiteSearch && <div className="mt-2 flex justify-end" onClick={e => e.stopPropagation()}>
                      <InlineVerifyButton
                        place={place}
                        searchId={searchId}
                        onOpenModal={openMobileVerify}
                      />
                    </div>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className={`hidden md:block overflow-x-auto rounded-xl ${showDrawerCoachmark ? 'pb-32' : ''}`}>
            <table className="min-w-full border-collapse">
              <thead className="sticky top-0 bg-white/80 backdrop-blur-md border-b-2 border-[#F0EDE8] z-10">
                <tr>
                  {/* Select-all checkbox */}
                  {!isNoWebsiteSearch && (
                    <th className="w-10 px-3 py-3">
                      <div
                        className={`w-[17px] h-[17px] rounded flex items-center justify-center border cursor-pointer transition-all ${
                          allChecked ? 'bg-[#8A3033] border-[#8A3033]' : 'bg-white border-[#D1D5DB]'
                        }`}
                        onClick={toggleAll}
                      >
                        {allChecked && (
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l2.5 2.5L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-[10.5px] font-bold text-[#9CA3AF] uppercase tracking-wide">
                    Business
                  </th>
                  {showWebsiteMatch && (
                    <th
                      className="px-4 py-3 text-left text-[10.5px] font-bold text-[#9CA3AF] uppercase tracking-wide min-w-[200px]"
                      title="Best matches have website evidence for your target services. Partial matches fit the Maps category but had limited website match evidence."
                    >
                      Website match
                    </th>
                  )}
                  {isNoWebsiteSearch && (
                    <th className="min-w-[190px] px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-wide text-[#9CA3AF]">
                      Phone
                    </th>
                  )}
                  <th
                    className="px-4 py-3 text-left text-[10.5px] font-bold text-[#9CA3AF] uppercase tracking-wide"
                    title={isNoWebsiteSearch ? undefined : 'Green = verified email · Dark = has email · Gray = not yet searched · Light gray = no email found'}
                  >
                    Decision makers
                  </th>
                  {!isNoWebsiteSearch && (
                    <th className="px-4 py-3 text-left text-[10.5px] font-bold text-[#9CA3AF] uppercase tracking-wide min-w-[220px]">
                      Emails
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-[10.5px] font-bold text-[#9CA3AF] uppercase tracking-wide">
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {loading
                  ? Array.from({ length: pagination.itemsPerPage }).map((_, i) => (
                      <tr key={`skeleton-${i}`} className="border-b border-[#F7F4F0]">
                        <td className="px-3 py-4">
                          <div className="w-[17px] h-[17px] rounded bg-[#F0EDE8] animate-pulse" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="h-5 w-28 bg-[#F0EDE8] animate-pulse rounded-full" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-start gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-[#F0EDE8] animate-pulse flex-shrink-0" />
                            <div className="space-y-2 flex-1">
                              <div className="h-3.5 w-40 bg-[#F0EDE8] animate-pulse rounded" />
                              <div className="h-2.5 w-24 bg-[#F0EDE8] animate-pulse rounded" />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#F0EDE8] animate-pulse flex-shrink-0" />
                            <div className="space-y-1.5">
                              <div className="h-3 w-24 bg-[#F0EDE8] animate-pulse rounded" />
                              <div className="h-2.5 w-16 bg-[#F0EDE8] animate-pulse rounded" />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1.5">
                            <div className="h-3 w-32 bg-[#F0EDE8] animate-pulse rounded" />
                            <div className="h-2.5 w-20 bg-[#F0EDE8] animate-pulse rounded" />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="h-3 w-20 bg-[#F0EDE8] animate-pulse rounded" />
                        </td>
                      </tr>
                    ))
                  : places.map((place, rowIndex) => (
                      <CompactResultRow
                        key={place.id}
                        place={place}
                        onViewDetails={() => handleViewDetails(place)}
                        searchId={searchId}
                        isSelected={selectedIds.has(place.id)}
                        onToggle={toggleSelect}
                        hints
                        showRating
                        isFirstRow={rowIndex === 0}
                        showDrawerCoachmark={Boolean(rowIndex === 0 && showDrawerCoachmark)}
                        onDismissDrawerCoachmarkGotIt={onDismissDrawerCoachmarkGotIt}
                        balance={balance}
                        onUpgradeClick={onUpgradeClick}
                        currentPlan={currentPlan}
                        autoFindMode={autoFindMode}
                        autoFindSuccessLimit={autoFindSuccessLimit}
                        autoFindSuccessCount={autoFindSuccessCount}
                        autoFindAttemptCount={autoFindAttemptCount}
                        autoFindNoEmailCount={autoFindNoEmailCount}
                        autoFindCatchAllCount={autoFindCatchAllCount}
                        autoFindGuardrailReason={autoFindGuardrailReason}
                        shortLabels={shortLabels}
                        showWebsiteMatch={showWebsiteMatch}
                        isNoWebsiteSearch={isNoWebsiteSearch}
                        selectable={!isNoWebsiteSearch}
                      />
                    ))}
              </tbody>
            </table>
          </div>
        </div>

        {pagination.totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={onPageChange}
              disabled={loading}
            />
            <div className="text-sm text-gray-500 text-center mt-2">
              Showing {((pagination.page - 1) * pagination.itemsPerPage) + 1} to{' '}
              {Math.min(pagination.page * pagination.itemsPerPage, pagination.totalItems)} of{' '}
              {pagination.totalItems} results
            </div>
          </div>
        )}
      </Card>

      <ResultsDrawer
        place={mergeDrawerPlace(
          drawerPlace ? (places.find(p => p.id === drawerPlace.id) ?? drawerPlace) : null,
          drawerPlaceDetail
        )}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        searchId={searchId}
        shortLabels={shortLabels}
        onUpgradeClick={onUpgradeClick}
        currentPlan={currentPlan}
        autoFindMode={autoFindMode}
        autoFindSuccessLimit={autoFindSuccessLimit}
        autoFindSuccessCount={autoFindSuccessCount}
        autoFindAttemptCount={autoFindAttemptCount}
        autoFindNoEmailCount={autoFindNoEmailCount}
        autoFindCatchAllCount={autoFindCatchAllCount}
        autoFindGuardrailReason={autoFindGuardrailReason}
      />

      <FloatingSelectionBar
        selectedIds={selectedIds}
        places={places}
        onExport={() => onExport('csv')}
        onClear={() => setSelectedIds(new Set())}
        searchId={searchId}
      />

      {mobileVerifyOpen && (
        <EmailVerificationModal
          isOpen
          onClose={closeMobileVerify}
          personEmails={mobileVerifyPersonEmails}
          placeEmails={mobileVerifyPlaceEmails}
          emailType="both"
          searchId={searchId}
          bulkMode={false}
          businessName={mobileVerifyBusinessName}
          skipInitialSelection
        />
      )}
    </>
  );
};

export default ResultsTable;
